import fp from "lodash/fp";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { branch, compose, defaultProps, lifecycle, mapProps, renderComponent, setPropTypes, withStateHandlers } from "recompose";
import { bindActionCreators } from "redux";
import { getCpdActivityClassName, getCpdActivityTitle, getCpdActivityTransform, mapCpdPoint, notSortableColumns } from "../../../../../constants";
import { decodeIfString, fullName } from "../../../../../services";
import { cpdCategorysActions } from "../../../../../state/modules/areas-of-advice";
import { publisherActions } from "../../../../../state/modules/publisher";
import { Icon } from "../../../../components";
import Loading from "../../../loading";

const SortingArrow = ({ field, sorting }) => {
  if (sorting[field] === "asc") {
    return <Icon icon="arrow-up-medium" />;
  } else if (sorting[field] === "desc") {
    return <Icon icon="arrow-down-medium" />;
  } else {
    return (
      <span>
        <Icon icon="arrow-down-medium" />
        <Icon icon="arrow-up-medium" />
      </span>
    );
  }
};

const CpdActivityTable = ({
  cpdActivity,
  cpdActivityMap,
  sort,
  sorting,
  sortBy,
  isFetchingCpdPoints,
  className,
  unclickable
}) => (
    <table className={className}>
      <thead>
        <tr>
          {fp.flow(
            fp.keys,
            fp.map(key => (
              <td
                key={key}
                className={getCpdActivityClassName(key)(cpdActivityMap)({}) + (unclickable ? " unclickable" : "")}
                onClick={e => sortBy && sort(key, sorting)}
                style={{ textAlign: 'center' }}
              >
                {getCpdActivityTitle(key)(cpdActivityMap)}
                {(!notSortableColumns.includes((getCpdActivityTitle(key)(cpdActivityMap))) && sortBy) ? '\u00A0' : ''}
                {(!notSortableColumns.includes((getCpdActivityTitle(key)(cpdActivityMap))) && sortBy) ? <SortingArrow field={key} sorting={sorting} /> : null}
              </td>
            ))
          )(cpdActivityMap)}
        </tr>
      </thead>
      <tbody>
        {cpdActivity.length ? (
          fp.map(({ id, ...cpdPoint }) => (
            <tr key={`${id}`}>
              {fp.flow(
                fp.keys,
                fp.map(key => (
                  <td
                    key={`${id}-${key}`}
                    className={getCpdActivityClassName(key)(cpdActivityMap)(
                      cpdPoint
                    )}
                    style={{ textAlign: 'center' }}
                  >
                    {decodeIfString(getCpdActivityTransform(key)(cpdActivityMap)(cpdPoint))}
                  </td>
                ))
              )(cpdActivityMap)}
            </tr>
          ))(cpdActivity)
        ) : (
            <tr>
              <td colSpan={fp.keys(cpdActivityMap).length}>No CPD Activity</td>
            </tr>
          )}
        {isFetchingCpdPoints ? (
          <tr>
            <td colSpan={7} className="text-center">
              <Loading />
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );

const mapStateToProps = ({ cpdCategorys, publisher, cpdPoints, user }) => ({
  user: user.currentUser,
  cpdCategorys: cpdCategorys.cpdCategorys,
  publishers: publisher.publishers,
  isFetchingCpdCategorys: cpdCategorys.isFetching,
  isFetchingPublishers: publisher.isFetching,
  isFetchingCpdPoints: cpdPoints.isFetching
});

const mapDispatchToProps = dispatch => ({
  getCpdCategorys: bindActionCreators(
    cpdCategorysActions.cpdCategorys.get,
    dispatch
  ),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch)
});

export default compose(
  setPropTypes({
    cpdActivity: PropTypes.array,
    take: PropTypes.number,
    sortBy: PropTypes.func
  }),
  defaultProps({
    cpdActivity: [],
    take: 5
  }),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategorys,
        isFetchingCpdCategorys,
        getCpdCategorys,
        publishers,
        isFetchingPublishers,
        getPublishers
      } = this.props;
      if (!cpdCategorys.length && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }

      if (!publishers.length && !isFetchingPublishers) {
        getPublishers();
      }
    }
  }),
  branch(
    ({ isFetchingCpdCategorys, isFetchingPublishers, isFetchingCpdPoints, cpdActivity }) =>
      isFetchingCpdCategorys || isFetchingPublishers || (isFetchingCpdPoints && cpdActivity.length === 0),
    renderComponent(Loading)
  ),
  withStateHandlers(
    {
      sorting: {}
    },
    {
      sort: (prevState, { sortBy }) => (field, sorting) => {
        let nextSortOrder = null;
        if (!sorting[field]) {
          nextSortOrder = "asc";
        }

        if (sorting[field] === "asc") {
          nextSortOrder = "desc";
        }

        if (sorting[field] === "desc") {
          nextSortOrder = undefined;
        }

        sortBy(field, nextSortOrder);

        return {
          sorting: { [field]: nextSortOrder }
        };
      }
    }
  ),
  mapProps(({ user, cpdCategorys, cpdActivity, publishers, ...props }) => ({
    ...props,
    cpdCategorys,
    publishers,
    cpdActivity: fp.flow(
      fp.map(
        mapCpdPoint(cpdCategorys, publishers),
      ),
      fp.map(
        (activity) => {
          return {
            ...activity,
            // Show login user name if no user name provided for the activity.
            userName: activity.userName || fullName(user),
            showContentLink: true
          }
        }
      )
    )(
      fp.uniqBy(
        ({ userCpdPointId, cpdCategoryId }) => ({ userCpdPointId, cpdCategoryId })
      )(cpdActivity)
    )
  }))
)(CpdActivityTable);



// WEBPACK FOOTER //
// ./src/containers/pages/app/professional-profile/components/cpd-activity-table.js